import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useFetch } from "../../../services/hooks/useFetch";
import { apiPrefix } from "../../../utils/main";
import InfoIcon from "@mui/icons-material/Info";
import DOMPurify from "dompurify";
import { ProjectContext } from "../../../contexts/ProjectContext";
import CloseArrowTooltip from "../../../components/CloseArrowTooltip";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import OfflineBoltOutlinedIcon from "@mui/icons-material/OfflineBoltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { HTTPMethod } from "@fusionauth/typescript-client";

const lf = new Intl.ListFormat("en");

const areKeywordsInvalid = (keywords: Keyword[]) => {
  let missingValues = false;

  for (let i = 0; i < keywords.length; i++) {
    if (keywords[i].phrase === "" || keywords[i].category === "") {
      missingValues = true;
      break;
    }
  }
  return missingValues;
};

export default function PrioritizationPage() {
  const theme = useTheme();

  const { fetch: getAllKeywords } = useFetch<Keyword[]>();
  const { update: bulkUpdateKeywords } = useFetch<void, KeywordUpdateRequest>();
  const { fetch: fetchPreview } = useFetch<
    KeywordPreview,
    KeywordPreviewRequest
  >();
  const { id: projectId, isLocked } = useContext(ProjectContext)!;

  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [updateDetails, setUpdateDetails] = useState<string>("");
  const [previewData, setPreviewData] = useState<
    Record<number, KeywordPreview>
  >([]);
  const [loadingPreview, setLoadingPreview] = useState<Set<number>>(
    new Set<number>(),
  );
  const [sortAscending, setSortAscending] = useState(true);

  const [editMode, setEditMode] = useState(false);
  const [refreshRequired, setRefreshRequired] = useState(true);

  const [displayErrors, setDisplayErrors] = useState(false);

  const getFormattedCategory = function (category: string): string {
    switch (category) {
      case "adverse_event":
        return "Adverse Event";

      case "performance":
        return "Performance";

      case "clinical":
        return "Clinical Benefit";

      case "device_identification":
        return "Device Identification";

      case "other":
        return "Other";

      default:
        return "";
    }
  };

  useEffect(() => {
    if (projectId && refreshRequired) {
      void getAllKeywords(`${apiPrefix}/projects/${projectId}/keywords`, {
        postResponseAction: (data) => {
          setKeywords(data || []);
        },
      });
      setRefreshRequired(false);
    }
  }, [projectId, refreshRequired, getAllKeywords]);

  return (
    <Grid container direction="column" className="company-settings-item">
      <div className="company-settings-item">
        <Grid>
          <Typography variant="h4">Prioritization</Typography>
        </Grid>
        <br />
        <Grid
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ backgroundColor: "rgba(0, 180, 215, 0.1)" }}
        >
          <Alert className="prioritization-info-text" severity="info">
            <AlertTitle>How 3Aware Prioritization Works</AlertTitle>
            Using your selections below, 3Aware will assign a priority score to
            clinical notes and patient visits. Records with high priority will
            be displayed with this icon:
            <OfflineBoltOutlinedIcon
              className="icon"
              sx={{ color: theme.palette.info.main }}
            />
          </Alert>
        </Grid>
        <br />
        <Grid>
          <Typography variant="h6">Important Keywords</Typography>
          <Typography variant="body2">
            Project-level keywords for 3Aware to use when prioritizing each
            patient’s visits and clinical notes. Each keyword is assigned a
            category based on the corresponding outcome measure.
          </Typography>
        </Grid>
        <br />
        {editMode ? (
          <Grid>
            <Typography variant="body2">* Required fields</Typography>
          </Grid>
        ) : (
          <Grid display="flex" flexDirection="row" alignItems="center">
            <Typography variant="body2">Sort Keywords</Typography>
            <IconButton onClick={() => setSortAscending(!sortAscending)}>
              {sortAscending ? (
                <ArrowUpwardIcon fontSize="small" />
              ) : (
                <ArrowDownwardIcon fontSize="small" />
              )}
            </IconButton>
          </Grid>
        )}
        <br />
        <Grid>
          <Stack
            direction="column"
            divider={
              <Divider sx={{ marginTop: "1rem", marginBottom: "1rem" }} />
            }
          >
            {keywords
              .sort((a: Keyword, b: Keyword): number => {
                if (!editMode) {
                  const phraseA = a.phrase.toLowerCase();
                  const phraseB = b.phrase.toLowerCase();
                  if (sortAscending) {
                    return phraseA.localeCompare(phraseB);
                  } else {
                    return phraseB.localeCompare(phraseA);
                  }
                } else {
                  return 0;
                }
              })
              .map((value, index) => {
                if (value.forDelete) {
                  return <div key={`row-${index}`}></div>;
                }

                return editMode ? (
                  <Grid
                    container
                    direction="row"
                    justifyContent={"center"}
                    alignItems={"center"}
                    key={`row-${index}`}
                    rowSpacing={1.5}
                    columnSpacing={2}
                    columns={{ xs: 4, sm: 8, md: 12, lg: 12, xl: 12 }}
                  >
                    <Grid size={{ xs: 4, sm: 4, md: 5, lg: 5, xl: 5 }}>
                      <TextField
                        required
                        fullWidth
                        label="Keyword"
                        value={value.phrase}
                        error={displayErrors && value.phrase === ""}
                        helperText={
                          displayErrors && value.phrase === ""
                            ? "This field is required"
                            : ""
                        }
                        onChange={(event) => {
                          const localKeywords = structuredClone(keywords);
                          localKeywords[index].phrase = event.target.value;
                          setKeywords(localKeywords);
                          setPreviewData((prevItems) => {
                            const newData = { ...prevItems };
                            delete newData[index];
                            return newData;
                          });
                        }}
                        slotProps={{ input: { readOnly: !editMode } }}
                      />
                    </Grid>
                    <Grid size={4}>
                      <FormControl
                        error={displayErrors && value.category === ""}
                        fullWidth
                      >
                        <InputLabel
                          required
                          shrink
                          htmlFor={"select-category-" + index}
                        >
                          Category
                        </InputLabel>
                        <Select
                          required
                          label="Category"
                          value={value.category}
                          onChange={(event) => {
                            const localKeywords = structuredClone(keywords);
                            localKeywords[index].category = event.target.value;
                            setKeywords(localKeywords);
                            setPreviewData((prevItems) => {
                              const newData = { ...prevItems };
                              delete newData[index];
                              return newData;
                            });
                          }}
                          inputProps={{ id: "select-category-" + index }}
                          readOnly={!editMode}
                          variant="outlined"
                        >
                          <MenuItem value="adverse_event">
                            Adverse Event
                          </MenuItem>
                          <MenuItem value="clinical">Clinical Benefit</MenuItem>
                          <MenuItem value="device_identification">
                            Device Identification
                          </MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                          <MenuItem value="performance">Performance</MenuItem>
                        </Select>
                        {displayErrors && value.category === "" && (
                          <FormHelperText>
                            This field is required
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid
                      container
                      size={{ xs: 3, sm: 7, md: 2, lg: 2, xl: 2 }}
                      spacing={0.5}
                      direction="row"
                      alignItems="center"
                    >
                      <Grid>
                        <ToggleButtonGroup
                          color="primary"
                          value={`${value.exact}`}
                          exclusive
                          onChange={(_event, newMatchMode: string) => {
                            // Clicking on a button that is already selected sets newMatchMode = null
                            if (newMatchMode === null) return;

                            const localKeywords = structuredClone(keywords);
                            localKeywords[index].exact =
                              newMatchMode === "true";
                            setKeywords(localKeywords);
                            setPreviewData((prevItems) => {
                              const newData = { ...prevItems };
                              delete newData[index];
                              return newData;
                            });
                          }}
                          aria-label="Match Mode"
                        >
                          <ToggleButton value="true">Exact</ToggleButton>
                          <ToggleButton value="false">Fuzzy</ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>
                      <Grid display="flex">
                        <Tooltip
                          title="Exact match will only prioritize visits and notes containing this exact keyword. Fuzzy match will also prioritize slight variations of this keyword."
                          placement="top-end"
                          arrow
                        >
                          <InfoIcon sx={{ color: theme.palette.info.main }} />
                        </Tooltip>
                      </Grid>
                    </Grid>
                    <Grid size={1}>
                      <IconButton
                        onClick={() => {
                          const localKeywords = structuredClone(keywords);
                          if (!localKeywords[index].id) {
                            localKeywords.splice(index, 1);
                          } else {
                            localKeywords[index].forDelete = true;
                          }
                          setKeywords(localKeywords);
                          setDisplayErrors(false);
                        }}
                      >
                        <CloseIcon sx={{ color: "rgba(161, 38, 16, 1)" }} />
                      </IconButton>
                    </Grid>
                    <Grid
                      size={{
                        xs: 1,
                        sm: 1.5,
                        md: 1.5,
                        lg: 1.25,
                        xl: 1,
                      }}
                    >
                      <Button
                        variant="contained"
                        disabled={!value.phrase || !!previewData[index]}
                        onClick={() => {
                          loadingPreview.add(index);
                          setLoadingPreview(loadingPreview);
                          void fetchPreview(
                            `${apiPrefix}/projects/${projectId}/keywords/counts?phrase=${value.phrase}&exact=${value.exact}`,
                            {
                              postResponseAction: (data) => {
                                setPreviewData({
                                  ...previewData,
                                  [index]: data!,
                                });
                                loadingPreview.delete(index);
                                setLoadingPreview(loadingPreview);
                              },
                            },
                          );
                        }}
                      >
                        Preview
                      </Button>
                    </Grid>
                    <Grid
                      size={{
                        xs: 3,
                        sm: 6.5,
                        md: 10.5,
                        lg: 10.75,
                        xl: 11,
                      }}
                    >
                      {loadingPreview.has(index) && <CircularProgress />}
                      {!!previewData[index] && (
                        <Typography
                          variant="body2"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              value.exact
                                ? `<strong>Exact</strong> match for <highlight>${value.phrase}</highlight> occurs <strong>${previewData[index].wordCount}</strong> times in <strong>${previewData[index].documentCount}</strong> documents in this project.`
                                : `<strong>Fuzzy</strong> match for ${
                                    value.phrase
                                  } will match ${lf.format(
                                    previewData[index].matchedWords.slice(0, 5),
                                  )} <strong>${
                                    previewData[index].wordCount
                                  }</strong> times in <strong>${
                                    previewData[index].documentCount
                                  }</strong> documents in this project.`,
                              {
                                ALLOWED_TAGS: ["strong", "highlight"],
                                RETURN_TRUSTED_TYPE: true,
                              },
                            ),
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                ) : (
                  <Grid
                    container
                    direction="row"
                    justifyContent={"center"}
                    alignItems={"flex-end"}
                    key={`row-${index}`}
                    columnSpacing={4}
                    rowSpacing={1.5}
                    columns={12}
                  >
                    <Grid size={6}>
                      <Typography variant="caption">Keyword *</Typography>
                      <Typography variant="body1" sx={{ width: "20ch" }}>
                        {value.phrase}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption">Category *</Typography>
                      <Typography variant="body1" sx={{ width: "50ch" }}>
                        {getFormattedCategory(value.category)}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2">
                        {value.exact
                          ? "Only exact matches"
                          : "Slight variations included"}
                      </Typography>
                    </Grid>
                    <Grid size={6} />
                  </Grid>
                );
              })}
          </Stack>
        </Grid>
        <br />
        {!editMode || (
          <Grid>
            <Button
              variant="contained"
              onClick={() => {
                if (areKeywordsInvalid(keywords)) {
                  setDisplayErrors(true);
                  return;
                } else {
                  setDisplayErrors(false);
                }

                const localKeywords = structuredClone(keywords);
                localKeywords.push({
                  phrase: "",
                  category: "",
                  projectId: projectId,
                  exact: true,
                  forDelete: false,
                });
                setKeywords(localKeywords);
              }}
            >
              Add Keyword
            </Button>
          </Grid>
        )}
        {editMode && (
          <>
            <br />
            <Grid>
              <TextField
                required
                variant="outlined"
                label="Note/Reason for Updating the Prioritization"
                className="settings-text-input"
                sx={{ width: "50ch" }}
                value={updateDetails}
                error={displayErrors && updateDetails === ""}
                helperText={
                  displayErrors && updateDetails === ""
                    ? "This field is required"
                    : ""
                }
                onChange={(event) => {
                  setUpdateDetails(event.target.value);
                }}
              />
            </Grid>
            <br />
          </>
        )}
        <Grid container justifyContent={"right"}>
          {(editMode && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setRefreshRequired(true);
                  setUpdateDetails("");
                  setDisplayErrors(false);
                  setEditMode(false);
                  setPreviewData([]);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="update-button"
                sx={{ marginLeft: "5px" }}
                onClick={() => {
                  if (areKeywordsInvalid(keywords) || updateDetails === "") {
                    setDisplayErrors(true);
                    return;
                  }

                  void bulkUpdateKeywords({
                    path: `${apiPrefix}/projects/${projectId}/keywords/bulk`,
                    body: {
                      reason: updateDetails,
                      keywords: keywords,
                    },
                    method: HTTPMethod.PUT,
                    postResponseAction: () => {
                      setEditMode(false);
                      setRefreshRequired(true);
                      setUpdateDetails("");
                      setPreviewData([]);
                    },
                  });
                }}
              >
                Update
              </Button>
            </>
          )) || (
            <CloseArrowTooltip
              title={isLocked ? "This project is locked." : ""}
            >
              <span>
                <Button
                  variant="contained"
                  onClick={() => setEditMode(true)}
                  disabled={isLocked}
                >
                  Edit
                </Button>
              </span>
            </CloseArrowTooltip>
          )}
        </Grid>
      </div>
    </Grid>
  );
}
